import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { groupBy } from "@suse-o11y/group-by";
import { IconDragHandle, IconEllipsisOutlined } from "@suse-o11y/icons";
import { map } from "@suse-o11y/map";
import { pickBy } from "@suse-o11y/pick-by";
import { pipe } from "@suse-o11y/pipe";
import { prop } from "@suse-o11y/prop";
import { px } from "@suse-o11y/px";
import { refId } from "@suse-o11y/ref-id";
import { reorder } from "@suse-o11y/reorder";
import { type Monitor, effect, useComputed, useMonitor } from "@suse-o11y/signal";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import { splice } from "@suse-o11y/splice";
import { toArray } from "@suse-o11y/to-array";
import { toKeys } from "@suse-o11y/to-keys";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { DragAndDrop } from "~/components/drag-and-drop";
import { useDraggableContext } from "~/components/drag-and-drop/context/draggable";
import { Draggable } from "~/components/drag-and-drop/draggable";
import { DraggablePlaceholder } from "~/components/drag-and-drop/draggable-placeholder";
import { Droppable } from "~/components/drag-and-drop/droppable";
import { DragHandle } from "~/components/drag-and-drop/handle";
import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";
import { Table } from "~/components/table";
import { TableBody } from "~/components/table/body";
import { TableHead } from "~/components/table/head";
import { TableHeadCell } from "~/components/table/head-cell";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { SetupDashboardVariableDrawer } from "../_setup-dashboard-variable-drawer";

import type { FormState } from "./_form-state";
import { VariableRow, type VariableRowProps } from "./_variable-row";

const OBSERVABILITY_PROMQL_VARIABLES_SET = new Set<string>(OBSERVABILITY_PROMQL_VARIABLES);

function validateVariables(monitor: Monitor, state: FormState): void {
	const variables = state.getElements(monitor);

	const duplicates = pipe(
		variables,
		map((variable) => variable.getInput(monitor)),
		groupBy(prop("name")),
		pickBy((list) => list.length > 1),
		toKeys(),
	);

	for (const variable of variables) {
		const name = variable.getInput(monitor).name.trim();
		const error = duplicates.includes(name)
			? "Duplicated variable name"
			: OBSERVABILITY_PROMQL_VARIABLES_SET.has(name)
				? "Built-in variables cannot be overridden"
				: null;

		variable.setError(error);
	}
}

interface VariablesTableProps {
	timeRange: TimeRange;
	variables: FormState;
}

export const VariablesTable: React.FC<VariablesTableProps> = ({ timeRange, variables }) => {
	const droppableId = React.useId();
	const firstRowRef = React.useRef<HTMLDivElement>(null);
	const [gridTemplateColumns, setGridTemplateColumns] = React.useState<string>();
	const allVariables = useComputed((monitor) => variables.getElements(monitor), [variables]);

	React.useEffect(() => effect((monitor) => validateVariables(monitor, variables)), [variables]);

	return (
		<DragAndDrop
			onBeforeDragStart={() => {
				// persist the columns width before dragging starts so the table layout stays consistent during d&d
				if (firstRowRef.current) {
					const columns = toArray(firstRowRef.current.children).map((column) => {
						/**
						 * The .getBoundingClientRect() should be used instead of .clientWidth because it returns a fractional value
						 * which is more precise and prevents layout shifts when the width is not an integer.
						 */
						const { width } = column.getBoundingClientRect();

						return px(width);
					});

					setGridTemplateColumns(columns.join(" "));
				}
			}}
			onDragEnd={({ source, destination }) => {
				variables.setElements((current) => reorder(current, source.index, destination?.index ?? source.index));
				setGridTemplateColumns(undefined);
			}}
		>
			<Table // color the background so it contrasts when d&d
				className="grid-cols-[auto_auto_auto_max-content_minmax(200px,1fr)_auto] bg-grey-300"
				style={{ gridTemplateColumns }}
			>
				<TableHead>
					<TableHeadCell className="col-start-2">Name</TableHeadCell>
					<TableHeadCell>Display Label</TableHeadCell>
					<TableHeadCell>Type</TableHeadCell>
					<TableHeadCell>Description</TableHeadCell>
				</TableHead>

				<Droppable
					asChild
					direction="vertical"
					draggableClone={<ClonedVariableRow gridTemplateColumns={gridTemplateColumns} variables={variables} />}
					droppableId={droppableId}
				>
					<TableBody each={allVariables}>
						{(variable, index, arr) => (
							<Draggable asChild index={index} isDragDisabled={arr.length < 2} key={refId(variable)}>
								<DraggableVariableRow
									handle={
										arr.length < 2 ? null : (
											<TooltipBoundary disableHoverableContent>
												<TooltipTrigger asChild>
													<DragHandle className="button button-square button-xs">
														<IconDragHandle className="text-grey-500" />
													</DragHandle>
												</TooltipTrigger>

												<Tooltip className="tooltip/tooltip">Drag to reorder</Tooltip>
											</TooltipBoundary>
										)
									}
									index={index}
									ref={index === 0 ? firstRowRef : undefined}
									timeRange={timeRange}
									variable={variable}
									variables={variables}
								/>
							</Draggable>
						)}

						<DraggablePlaceholder />
					</TableBody>
				</Droppable>
			</Table>
		</DragAndDrop>
	);
};

interface DraggableVariableRowProps extends Omit<VariableRowProps, "actions">, VariablesTableProps {
	index: number;
}

const DraggableVariableRow = React.forwardRef<React.ComponentRef<typeof VariableRow>, DraggableVariableRowProps>(
	({ timeRange, variable, variables, index, ...props }, ref) => {
		const monitor = useMonitor();

		const [isSetupDrawerOpen, setIsSetupDrawerOpen] = React.useState(false);

		const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
		const focusInvalidRef = useOnInvalidFormFocus<React.ComponentRef<typeof DropdownMenuTrigger>>(variable, () => {
			setIsDropdownOpen(true);
		});

		return (
			<VariableRow
				ref={ref}
				variable={variable}
				{...props}
				actions={
					<DialogBoundary onOpenChange={setIsSetupDrawerOpen} open={isSetupDrawerOpen}>
						<DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen || isSetupDrawerOpen}>
							<DropdownMenuTrigger
								asChild={false}
								className="button button-square button-xs"
								ref={focusInvalidRef}
								type="button"
							>
								<IconEllipsisOutlined className="text-[20px]" />
							</DropdownMenuTrigger>

							<DropdownMenuContent className="z-drawer!">
								<DialogTrigger asChild>
									<DropdownMenuItem onSelect={(event) => event.preventDefault()}>Edit variable</DropdownMenuItem>
								</DialogTrigger>

								<DropdownMenuItem
									onSelect={() => {
										variables.setInput(splice(index, 1, []));
									}}
								>
									Delete variable
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<SetupDashboardVariableDrawer
							initial={variable.getInput(monitor)}
							onSave={(updated) => {
								variable.setInput(updated);
							}}
							timeRange={timeRange}
							variables={variables.getOutput(monitor) ?? []}
						/>
					</DialogBoundary>
				}
			/>
		);
	},
);

interface ClonedVariableRowProps extends Omit<VariablesTableProps, "timeRange"> {
	gridTemplateColumns: undefined | string;
	style?: React.CSSProperties;
}

const ClonedVariableRow = React.forwardRef<HTMLDivElement, ClonedVariableRowProps>(
	({ variables, gridTemplateColumns, style, ...props }, ref) => {
		const { source } = useDraggableContext();
		const variable = useComputed(
			(monitor) => variables.getElements(monitor).at(source.index),
			[variables, source.index],
		);

		if (variable == null) {
			return null;
		}

		return (
			<VariableRow
				className="items-center"
				handle={
					<span className="button button-square button-xs">
						<IconDragHandle className="text-grey-500" />
					</span>
				}
				ref={ref}
				style={{ gridTemplateColumns, ...style }}
				variable={variable}
				{...props}
				actions={
					<span className="button button-square button-xs">
						<IconEllipsisOutlined className="text-[20px]" />
					</span>
				}
			/>
		);
	},
);
